import { useMemo, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { MessageCircle, House, Building2, ScanFace } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { waLink } from "@/lib/site";

const PROPERTIES = [
    { value: "residencia", label: "Residência", icon: House },
    { value: "condominio-pequeno", label: "Condomínio pequeno", icon: Building2 },
    { value: "condominio-grande", label: "Condomínio grande", icon: Building2 },
    { value: "comercial", label: "Comercial", icon: Building2 },
];

const Calculator = () => {
    const [property, setProperty] = useState("residencia");
    const [cameras, setCameras] = useState(12);
    const [facial, setFacial] = useState(true);

    const propertyLabel = useMemo(
        () => PROPERTIES.find((p) => p.value === property)?.label ?? "",
        [property],
    );

    const message = `Olá! Montei um esboço no site: ${propertyLabel}, ~${cameras} câmeras 4K${facial ? " + acesso facial" : ""}. Quero um orçamento.`;

    return (
        <section id="simulador" className="relative py-24 lg:py-32 bg-panel/40 border-y border-white/10">
            <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
                <Reveal>
                    <Eyebrow>[ 02 — Simulador ]</Eyebrow>
                    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] text-white">
                        Monte o esboço do seu sistema
                        <span className="text-mint"> em 30 segundos.</span>
                    </h2>
                    <p className="mt-5 max-w-lg text-slate-400 leading-relaxed text-sm sm:text-base">
                        Diga o tipo de imóvel, quantas câmeras imagina e se
                        quer acesso facial. Nós traduzimos isso em uma mensagem
                        pronta para o orçamento.
                    </p>
                    <div className="mt-8 flex items-start gap-4 rounded-2xl border border-volt/25 bg-volt/5 p-5 max-w-lg">
                        <ScanFace size={20} className="text-volt shrink-0 mt-0.5" />
                        <p className="text-sm leading-relaxed text-slate-300">
                            A vistoria técnica é sem compromisso: avaliamos
                            pontos cegos, cabeamento e portarias antes de
                            fechar qualquer valor.
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.12}>
                    <div
                        data-testid="budget-calculator-container"
                        className="clip-corner border border-white/10 bg-panel p-7 sm:p-9 shadow-[0_0_80px_rgba(46,124,246,0.1)]"
                    >
                        <div>
                            <label
                                htmlFor="calc-property"
                                className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400"
                            >
                                Tipo de imóvel
                            </label>
                            <select
                                id="calc-property"
                                data-testid="calculator-property-type-select"
                                value={property}
                                onChange={(e) => setProperty(e.target.value)}
                                className="mt-3 w-full appearance-none rounded-xl border border-white/15 bg-ink px-4 py-3.5 text-sm text-slate-100 outline-none focus:border-volt transition-colors duration-200"
                            >
                                {PROPERTIES.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mt-7">
                            <div className="flex items-center justify-between">
                                <label
                                    htmlFor="calc-cameras"
                                    className="font-hud text-[10px] uppercase tracking-[0.25em] text-slate-400"
                                >
                                    Câmeras estimadas
                                </label>
                                <span
                                    data-testid="calculator-camera-value"
                                    className="font-hud text-2xl font-bold text-volt"
                                >
                                    {cameras}
                                </span>
                            </div>
                            <input
                                id="calc-cameras"
                                type="range"
                                min="4"
                                max="64"
                                step="2"
                                value={cameras}
                                data-testid="calculator-camera-count-slider"
                                onChange={(e) => setCameras(Number(e.target.value))}
                                className="mt-4 w-full cursor-pointer"
                            />
                            <div className="mt-1 flex justify-between font-hud text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                <span>4</span>
                                <span>64</span>
                            </div>
                        </div>

                        <div className="mt-7 flex items-center justify-between rounded-xl border border-white/10 bg-ink/60 px-5 py-4">
                            <div>
                                <p className="text-sm font-semibold text-slate-100">
                                    Acesso facial
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Liberar moradores por reconhecimento
                                </p>
                            </div>
                            <Switch
                                checked={facial}
                                onCheckedChange={setFacial}
                                data-testid="calculator-facial-toggle-switch"
                            />
                        </div>

                        <div className="mt-7 rounded-xl border border-mint/25 bg-mint/5 px-5 py-4">
                            <p className="font-hud text-[10px] uppercase tracking-[0.25em] text-mint">
                                Escopo inicial
                            </p>
                            <p
                                data-testid="calculator-scope-summary"
                                className="mt-2 font-display text-lg sm:text-xl font-bold text-white"
                            >
                                {propertyLabel} · {cameras} câmeras 4K
                                {facial ? " + acesso facial" : ""}
                            </p>
                        </div>

                        <a
                            href={waLink(message)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="calculator-submit-whatsapp"
                            className="mt-7 flex w-full items-center justify-center gap-3 rounded-full bg-mint px-6 py-4 font-hud text-xs font-bold uppercase tracking-[0.18em] text-ink hover:bg-volt transition-colors duration-200"
                        >
                            <MessageCircle size={16} strokeWidth={2.4} />
                            Receber orçamento no WhatsApp
                        </a>
                        <p className="mt-3 text-center font-hud text-[9px] uppercase tracking-[0.2em] text-slate-500">
                            Orçamento fechado após vistoria técnica
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};

export default Calculator;
